import {
    IContextualMenuItem,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CancelToken } from 'axios';
import { Banner } from '../../../Common/models/Banner';
import { HubIdTokenClaims } from '../../../HubLayout/util/authUtils';
import { usePromise } from '../../../Common/hooks/usePromise';
import {
    deleteBanner,
    getBannerData,
    setBannerData,
} from '../../../Common/api/ABHub/BannerApi';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { AuthContext } from '../../../HubLayout/models/AuthContext';
import { BannerCard } from './BannerCard';
import { useIsMobile } from '../../../Common/hooks/useIsMobile';

interface IBannerProps {}

export const BannerList: React.FC<IBannerProps> = (props: IBannerProps) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const authContext = useContext(AuthContext);
    const isMobile = useIsMobile();

    const [bannerList, setBannerList] = useState<Banner[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [collapsedBanners, setCollapsedBanners] = useState<Set<string>>(
        new Set()
    );
    const [deletingBannerId, setDeletingBannerId] = useState<string | null>(
        null
    );

    const getBannerList = useCallback(async (cancelToken?: CancelToken) => {
        try {
            setErrorMessage(null);
            const banners: Banner[] = await getBannerData(cancelToken);
            return banners;
        } catch (error) {
            setErrorMessage(
                `Failed to load banners: ${(error as Error).message}`
            );
            throw error;
        }
    }, []);

    const [list, error, isLoaded, startBannerList] = usePromise(
        getBannerList,
        true
    );

    const bannerTypeOptions: IDropdownOption[] = [
        { key: 0, text: 'General' },
        { key: 1, text: 'Warning' },
        { key: 2, text: 'Error' },
    ];

    const deleteBannerCallback = useCallback(
        async (id: string, cancelToken?: CancelToken) => {
            try {
                setErrorMessage(null);
                setSuccessMessage(null);
                const response = await deleteBanner(id, cancelToken);
                setSuccessMessage('Banner deleted successfully');
                startBannerList();
                setDeletingBannerId(null);
                return response;
            } catch (error) {
                setErrorMessage(
                    `Failed to delete banner: ${(error as Error).message}`
                );
                setDeletingBannerId(null);
                throw error;
            }
        },
        [startBannerList]
    );

    const saveBannerCallback = useCallback(
        async (banner: Banner, cancelToken?: CancelToken) => {
            try {
                setErrorMessage(null);
                setSuccessMessage(null);
                const response = await setBannerData(banner, cancelToken);
                setSuccessMessage(
                    banner.id && banner.id !== 'new-banner'
                        ? 'Banner updated successfully'
                        : 'Banner created successfully'
                );
                startBannerList();
                return response;
            } catch (error) {
                setErrorMessage(
                    `Failed to save banner: ${(error as Error).message}`
                );
                throw error;
            }
        },
        [startBannerList]
    );

    useEffect(() => {
        if (list) {
            // Convert date strings to Date objects
            const bannersWithDates = list.map((banner) => ({
                ...banner,
                startDate: new Date(banner.startDate),
                endDate: new Date(banner.endDate),
                createdDate: new Date(banner.createdDate),
                lastUpdatedDate: banner.lastUpdatedDate
                    ? new Date(banner.lastUpdatedDate)
                    : undefined,
            }));
            setBannerList(bannersWithDates);
            const allIds = new Set(
                bannersWithDates.map((b) => b.id).filter((id) => id) as string[]
            );
            setCollapsedBanners(allIds);
        }
    }, [list]);

    const toggleBannerCollapse = (bannerId: string) => {
        setCollapsedBanners((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(bannerId)) {
                newSet.delete(bannerId);
            } else {
                newSet.add(bannerId);
            }
            return newSet;
        });
    };

    const handleAddNewBanner = () => {
        const newBanner: Banner = {
            id: 'new-banner',
            title: '',
            message: '',
            isActive: false,
            createdBy: String(
                (
                    authContext.authContext.user
                        ?.idTokenClaims as HubIdTokenClaims
                )?.upn ??
                    authContext.authContext.user?.username ??
                    ''
            ).split('@')[0],
            createdDate: new Date(),
            startDate: new Date(),
            endDate: new Date(),
            type: 0,
            typeInt: 0,
            typeString: 'General',
        };
        setBannerList((prev) => [newBanner, ...prev]);
        setCollapsedBanners((prev) => {
            const newSet = new Set(prev);
            newSet.delete('new-banner');
            return newSet;
        });
    };

    const handleDeleteConfirmation = (id: string) => {
        setDeletingBannerId(id);
        deleteBannerCallback(id);
    };

    const handleSave = (banner: Banner) => {
        const updatedBanner: Banner = {
            ...banner,
            lastUpdatedBy: String(
                (
                    authContext.authContext.user
                        ?.idTokenClaims as HubIdTokenClaims
                )?.upn ??
                    authContext.authContext.user?.username ??
                    ''
            ).split('@')[0],
            lastUpdatedDate: new Date(),
        };
        saveBannerCallback(updatedBanner);
    };

    const handleCancel = (bannerId: string) => {
        if (bannerId === 'new-banner') {
            setBannerList((prev) => prev.filter((b) => b.id !== 'new-banner'));
        } else {
            startBannerList();
        }
    };

    const getMenuItems = (banner: Banner): IContextualMenuItem[] => {
        const items: IContextualMenuItem[] = [];

        if (banner.id !== 'new-banner') {
            items.push({
                key: 'delete',
                text: 'Delete',
                iconProps: { iconName: 'Delete' },
                onClick: () => {
                    if (banner.id) {
                        handleDeleteConfirmation(banner.id);
                    }
                },
            });
        }

        return items;
    };

    return (
        <div className="Banner-root">
            {errorMessage && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={true}
                    onDismiss={() => setErrorMessage(null)}
                    dismissButtonAriaLabel="Close"
                    styles={{ root: { marginBottom: '16px' } }}
                >
                    {errorMessage}
                </MessageBar>
            )}
            {successMessage && (
                <MessageBar
                    messageBarType={MessageBarType.success}
                    isMultiline={false}
                    onDismiss={() => setSuccessMessage(null)}
                    dismissButtonAriaLabel="Close"
                    styles={{
                        root: {
                            marginBottom: '16px',
                            backgroundColor: theme.palette.green,
                        },
                        text: {
                            color: theme.palette.white,
                        },
                        icon: {
                            color: theme.palette.white,
                        },
                        dismissal: {
                            color: theme.palette.white,
                        },
                    }}
                >
                    {successMessage}
                </MessageBar>
            )}

            {!isLoaded && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '24px',
                    }}
                >
                    <Spinner
                        size={SpinnerSize.large}
                        label="Loading banners..."
                    />
                </div>
            )}

            {isLoaded && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px',
                        }}
                    >
                        <Text
                            variant="medium"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            {
                                bannerList.filter((b) => b.id !== 'new-banner')
                                    .length
                            }{' '}
                            {bannerList.filter((b) => b.id !== 'new-banner')
                                .length === 1
                                ? 'Banner'
                                : 'Banners'}
                        </Text>
                        <PrimaryButton
                            text={isMobile ? 'Add' : 'Add Banner'}
                            iconProps={{ iconName: 'Add' }}
                            onClick={handleAddNewBanner}
                            styles={{
                                root: {
                                    backgroundColor: theme.palette.themePrimary,
                                    borderColor: theme.palette.themePrimary,
                                },
                            }}
                        />
                    </div>

                    {bannerList.length === 0 && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            No banners found. Click "Add Banner" to create one.
                        </MessageBar>
                    )}

                    <Stack tokens={{ childrenGap: 12 }}>
                        {bannerList.map((banner, index) => {
                            const isCollapsed = collapsedBanners.has(
                                banner.id!
                            );
                            const isDeleting = deletingBannerId === banner.id;
                            const isNew = banner.id === 'new-banner';

                            return (
                                <BannerCard
                                    key={banner.id || index}
                                    banner={banner}
                                    index={index}
                                    isCollapsed={isCollapsed}
                                    isDeleting={isDeleting}
                                    isNew={isNew}
                                    bannerTypeOptions={bannerTypeOptions}
                                    theme={theme}
                                    isMobile={isMobile}
                                    onToggleCollapse={() =>
                                        toggleBannerCollapse(banner.id!)
                                    }
                                    onSave={handleSave}
                                    onCancel={handleCancel}
                                    getMenuItems={getMenuItems}
                                />
                            );
                        })}
                    </Stack>
                </>
            )}
        </div>
    );
};
